import type {
  DeleteImportMBFileResponse,
  GetImportMBFileRequirementsResponse,
  GetImportMBFileResponse,
  PostImportMBFileResponse,
  SearchUnitRequest,
  SearchUnitRequestQueryParams,
  SearchUnitResponse,
} from '@appTypes/esb';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';

export const councilUnitsApi = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint POST /organizations/units/search
     */
    getCouncilUnits: builder.query<
      SearchUnitResponse,
      {
        params: SearchUnitRequestQueryParams;
        payload: SearchUnitRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/units/search`,
        body: arg.payload,
        method: 'POST',
        params: arg.params,
      }),
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        const tags = [
          tagFactory.UnitSearch.organizationGuid(arg.payload.organizationGuid),
        ];

        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/:organizationGuid/meritBadges/importFile
     */
    getImportFileStatus: builder.query<
      GetImportMBFileResponse,
      {
        orgGUID: string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGUID}/meritBadges/importFile`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        const tags = [tagFactory.ImportMBFile.organizationGuid(arg.orgGUID)];

        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/:organizationGuid/meritBadges/requirements/importFile
     */
    getImportFileRequirements: builder.query<
      GetImportMBFileRequirementsResponse,
      {
        orgGUID: string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGUID}/meritBadges/requirements/importFile`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        const tags = [tagFactory.ImportMBFile.requirements(arg.orgGUID)];

        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/organization/:organizationGuid/meritBadges/importFile/:importFileId/start
     */
    startMBFileProcessing: builder.mutation<
      PostImportMBFileResponse,
      {
        orgGUID: string;
        importFileID: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGUID}/meritBadges/importFile/${arg.importFileID}/start`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [tagFactory.ImportMBFile.organizationGuid(arg.orgGUID)],
    }),

    /**
     * @esbEndpoint DELETE /advancements/v2/organization/:organizationGuid/meritBadges/importFile/:importFileId
     */
    deleteMBFile: builder.mutation<
      DeleteImportMBFileResponse,
      {
        orgGUID: string;
        importFileID: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGUID}/meritBadges/importFile/${arg.importFileID}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tagFactory.ImportMBFile.organizationGuid(arg.orgGUID),
              tagFactory.ImportMBFile.requirements(arg.orgGUID),
            ],
    }),
    /**
     * @esbEndpoint POST /advancements/v2/organization/:organizationGuid/meritBadges/importFile
     */
    uploadMBFile: builder.mutation<
      void,
      {
        payload: FormData;
        orgGUID: string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGUID}/meritBadges/importFile`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tagFactory.ImportMBFile.organizationGuid(arg.orgGUID),
              tagFactory.ImportMBFile.requirements(arg.orgGUID),
            ],
    }),
  }),
});

export const {
  useGetCouncilUnitsQuery,
  useLazyGetCouncilUnitsQuery,
  useGetImportFileStatusQuery,
  useGetImportFileRequirementsQuery,
  useLazyGetImportFileRequirementsQuery,
  useStartMBFileProcessingMutation,
  useDeleteMBFileMutation,
  useUploadMBFileMutation,
} = councilUnitsApi;
